import { Data } from './interfaces';

export const isDateValid = (dateString : string) : string =>
{
    // First check for the pattern
    if(!/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(dateString)) 
    { // Should be forced by normalizer
        return 'Date must be formatted as yyyy/mm/dd';
    }

    // Parse the date parts to integers
    const parts = dateString.split('/');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    if (month === 0 || month > 12) 
    {
        return 'Invalid month';
    }

    const monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ];

    // Adjust for leap years
    if(year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) 
    {
        monthLength[1] = 29;
    }

    // Check the range of the day
    if (day === 0 && day > monthLength[month - 1]) 
    {
        return 'Invalid day';
    }

    return '';
};

export const isAgeValid = (dateString : string) : string => 
{
    // First check for the pattern
    if (!/^\d{4}\/\d{2}\/\d{2}$/.test(dateString)) 
    { // Should be forced by normalizer
        return 'Date must be formatted as yyyy/mm/dd';
    }

    // Parse the date parts to integers
    const parts = dateString.split('/');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    if (month === 0 || month > 12) 
    {
        return 'Invalid month';
    }

    const monthLength = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ];

    // Adjust for leap years
    if(year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) 
    {
        monthLength[1] = 29;
    }

    // Check the range of the day
    if (day === 0 || day > monthLength[month - 1]) 
    {
        return 'Invalid day';
    }

    // The getTime() method returns the number of milliseconds between midnight of January 1, 1970 and the specified date and the Date.now() method returns the number of milliseconds since January 1, 1970 00:00:00 UTC
    const birthdate = new Date(`${year}-${month}-${day}`);
    const diff = Date.now() - birthdate.getTime();
    const date = new Date(diff);
    const age = Math.abs(date.getUTCFullYear() - 1970);

    const minAge = 18;
    const maxAge = 100;

    if (age < minAge || age > maxAge) return `Age should be between ${minAge} and ${maxAge}`;

    return '';
};

export const isEmail = (value : string) => 
{
    const isValidEmailFormat = /\S+@\S+\.\S{2,}/.test(value.toLowerCase());
    const valueArray = value.toLowerCase().split('.');
    const lastIndex = valueArray.length - 1;
    const hasWrongDomains = valueArray[lastIndex] === 'or';

    return isValidEmailFormat && !hasWrongDomains ?
        '' :
        'Please ensure you have entered your email correctly before continuing';
};

export const isNotYahooEmail = (value: string) =>
{
    const emailFormat = value.split('@')[1]?.toLowerCase();
    const yahooEmail = emailFormat?.includes('yahoo') || emailFormat?.includes('rocketmail');

    return yahooEmail ? 
        'Due to an issue with Yahoo’s email servers, we are not accepting emails from their domain at this time. Please use another email.' :
        ''; 
};

export const isRequired = (value : string) => 
{
    if (value === '') return 'Required';
    
    return '';
};

type ValidatorFunction = (value : string) => string;

export const isChained = (validators : Array<ValidatorFunction>) => 
{
    return (value : string) : string =>
    {
        for (const fn of validators)
        {
            const error = fn(value);
            if (error !== '') return error;
        }

        return '';
    };
};

export const isFormValid = (formErrors : Data, requiredFields: Array<string>) : boolean =>
{
    for (const field of requiredFields)
    {
        if (formErrors[field] !== '') return false;
    }

    return true;
};

export const isInitialFormValid = (formValues : Data, requiredFields: Array<string>) : boolean =>
{
    for (const field of requiredFields)
    {
        if (!formValues[field]) return false;
    }

    return true;
};

export const purchasePriceValidator = (value : string) =>
{
    const v = parseFloat(value.slice(1).split(',').join(''));

    if (v < 50000) return 'Value should be at least $50,000';
    return '';
};

export const purchasePriceUpperValidator = (value : string) =>
{
    const v = parseFloat(value.slice(1).split(',').join(''));

    if (v > 50000000) return 'Value should be at most $50,000,000';
    return '';
};

export const isValidIncome = (value: string) => 
{
    const income = parseFloat(value.replace(/,/g, '').split('$')[1]);

    if (income < 1000) return 'In order to ensure your application is queued up properly please ensure you report accurate income';
    return '';
};